import { isAxiosError } from "axios";
import i18n, { t } from "i18next";
import axios from "../lib/axios";
import { CEID, Project, ProjectSupervisor } from "../types";

export const getProjectsForEmployee = async (
  employeeId: number,
  companyId: number,
): Promise<Project[]> => {
  try {
    const { data } = await axios.get<Project[]>("/api/projects-employees", {
      headers: {
        "Accept-Language": i18n.language,
      },
      params: {
        employeeId,
        companyId,
      },
    });

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getProjectsAvailableForUser = async (
  companyId?: number,
): Promise<Project[]> => {
  try {
    const { data } = await axios.get<Project[]>(
      "/api/projects-available-for-user",
      {
        headers: {
          "Accept-Language": i18n.language,
        },
        params: {
          companyId,
        },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getProjectsFromCompany = async (
  companyId?: number,
): Promise<Project[]> => {
  try {
    const { data } = await axios.get<Project[]>("/api/v2/projects", {
      headers: {
        "Accept-Language": i18n.language,
      },
      params: {
        companyId,
      },
    });

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getProjectEmployees = async (
  projectId: number,
): Promise<ProjectSupervisor[]> => {
  try {
    const { data } = await axios.get<ProjectSupervisor[]>(
      `/api/v2/projects/${projectId}/employees`,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const getProjectEmployeesAvailableToAssign = async (
  projectId: number,
): Promise<ProjectSupervisor[]> => {
  try {
    const { data } = await axios.get<ProjectSupervisor[]>(
      `/api/v2/projects/${projectId}/employees-available-to-assign`,
      {
        headers: {
          "Accept-Language": i18n.language,
        },
      },
    );

    return data;
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const assignEmployeesToProject = async (
  projectId: number,
  ceids: CEID[],
): Promise<void> => {
  try {
    await axios.post(`/api/v2/projects/${projectId}/assign-employees`, ceids, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};

export const unassignEmployeeToProject = async (
  projectId: number,
  ceid: CEID,
): Promise<void> => {
  try {
    await axios.post(`/api/v2/projects/${projectId}/unassign-employee`, ceid, {
      headers: {
        "Accept-Language": i18n.language,
      },
    });
  } catch (error) {
    let message = t("common.unknownErrorOccurred");

    if (isAxiosError(error)) {
      if (error.response) {
        if (error.response.status === 403) {
          message = t("noPermissions");
        } else if (error.response.data) {
          if (
            "errors" in error.response.data &&
            Array.isArray(error.response.data.errors) &&
            error.response.data.errors.length > 0 &&
            typeof error.response.data.errors[0] === "object" &&
            "message" in error.response.data.errors[0]
          ) {
            message = error.response.data.errors[0].message;
          } else if ("message" in error.response.data) {
            message = error.response.data.message;
          }
        }
      } else if (error.request) {
        message = t("common.noResponseFromServer");
      }
    }

    throw new Error(message);
  }
};
